import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { edgeErrorMessage } from '@/lib/edge-errors';
import { createPseudonymizer } from '@/lib/maaltall-agent/pseudonym';
import { runAgentTool, TOOL_LABELS, type AgentToolName } from '@/lib/maaltall-agent/tools';
import { useMaaltallAgentInputs } from '@/hooks/use-maaltall-agent-inputs';

type Block = { type: string; [key: string]: unknown };
type Message = { role: 'user' | 'assistant'; content: string | Block[] };

export interface AgentTurn {
  question: string;
  /** Svaret med ekte navn tilbake. Null mens agenten jobber. */
  answer: string | null;
  sources: Array<{ tool: AgentToolName; label: string }>;
  error?: string;
}

/** Samme grense som MAX_ROUNDS i edge function, så klienten stopper først. */
const MAX_ROUNDS = 8;

/**
 * Samtalen med tall-agenten (VIB-2992). Verktøyene kjøres her, med widgetenes
 * data, og alt som sendes er pseudonymisert. Samtalen holdes uendret og bare
 * legges til på, så modellens tenkeblokker sendes tilbake slik de kom.
 */
export function useMaaltallAgent() {
  const { inputs, people } = useMaaltallAgentInputs();
  const latest = useRef({ inputs, people });
  latest.current = { inputs, people };
  const messages = useRef<Message[]>([]);
  const [turns, setTurns] = useState<AgentTurn[]>([]);
  const [isPending, setIsPending] = useState(false);

  const update = (patch: Partial<AgentTurn>) =>
    setTurns((ts) => ts.map((t, i) => (i === ts.length - 1 ? { ...t, ...patch } : t)));

  async function ask(question: string) {
    const q = question.trim();
    if (!q || isPending) return;
    const pseudo = createPseudonymizer(latest.current.people);
    setIsPending(true);
    setTurns((ts) => [...ts, { question: q, answer: null, sources: [] }]);
    const used = new Set<AgentToolName>();
    const draft: Message[] = [...messages.current, { role: 'user', content: pseudo.scrub(q) }];
    try {
      for (let round = 0; round < MAX_ROUNDS; round++) {
        const { data, error } = await supabase.functions.invoke('maaltall-agent', { body: { messages: draft } });
        if (error) throw new Error(await edgeErrorMessage(error, data, 'Agenten svarte ikke.'));
        const content = (data?.content ?? []) as Block[];
        draft.push({ role: 'assistant', content });
        if (data?.stop_reason !== 'tool_use') {
          messages.current = draft;
          const text = content.filter((b) => b.type === 'text').map((b) => String(b.text)).join('\n').trim();
          update({
            answer: data?.stop_reason === 'refusal' || !text ? 'Agenten kunne ikke svare på dette spørsmålet.' : pseudo.restore(text),
            sources: [...used].map((tool) => ({ tool, label: TOOL_LABELS[tool] })),
          });
          return;
        }
        const results = content.filter((b) => b.type === 'tool_use').map((b) => {
          used.add(b.name as AgentToolName);
          return {
            type: 'tool_result',
            tool_use_id: String(b.id),
            content: pseudo.scrub(JSON.stringify(runAgentTool(String(b.name), latest.current.inputs))),
          };
        });
        draft.push({ role: 'user', content: results });
      }
      throw new Error('Spørsmålet krevde for mange oppslag. Prøv å stille det smalere.');
    } catch (e) {
      // Samtalen før spørsmålet beholdes, så neste spørsmål starter rent.
      update({ error: e instanceof Error ? e.message : 'Noe gikk galt.' });
    } finally {
      setIsPending(false);
    }
  }

  function reset() {
    messages.current = [];
    setTurns([]);
  }

  return { turns, ask, reset, isPending };
}
